import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Award,
  Download,
  ExternalLink,
  Search,
  CheckCircle,
  XCircle,
  Loader2,
  Trophy,
  Star,
  Share2,
  Copy,
  Check,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { certificatesApi } from '@/api/endpoints';
import type { MyCertificate } from '@/api/endpoints';
import { format } from 'date-fns';

export const MyCertificatesPage = () => {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [downloading, setDownloading] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedCertificate, setSelectedCertificate] = useState<MyCertificate | null>(null);

  const { data: certsRes, isLoading } = useQuery({
    queryKey: ['my-certificates', page],
    queryFn: () => certificatesApi.getMyCertificates({ page, per_page: 12 }),
    staleTime: 30000,
  });

  const certificates: MyCertificate[] = certsRes?.data?.data || [];
  const totalPages = certsRes?.data?.meta?.total_pages || 1;

  const handleDownload = async (certificate: MyCertificate) => {
    try {
      setDownloading(certificate.id);
      if (certificate.certificate_url) {
        window.open(certificate.certificate_url, '_blank');
      } else {
        const response = await certificatesApi.download(certificate.id);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `certificate-${certificate.certificate_number}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download certificate:', error);
    } finally {
      setDownloading(null);
    }
  };

  const handleCopyVerificationLink = (certificate: MyCertificate) => {
    const verifyUrl = `${window.location.origin}/verify/${certificate.certificate_number}`;
    navigator.clipboard.writeText(verifyUrl);
    setCopiedId(certificate.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getGradeColor = (grade?: string) => {
    switch (grade) {
      case 'A+':
      case 'A':
        return 'text-emerald-400 bg-emerald-500/10';
      case 'A-':
      case 'B+':
      case 'B':
        return 'text-teal-400 bg-teal-500/10';
      case 'B-':
      case 'C+':
      case 'C':
        return 'text-amber-400 bg-amber-500/10';
      default:
        return 'text-gray-400 bg-gray-500/10';
    }
  };

  const filteredCertificates = certificates.filter(
    (c) =>
      c.course_title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.certificate_number?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = {
    total: certificates.length,
    earned: certificates.length,
    topGrade: certificates.length > 0
      ? certificates.reduce((top, c) => {
          if (!c.grade) return top;
          const gradeOrder = ['A+', 'A', 'A-', 'B+', 'B', 'B-', 'C+', 'C', 'C-', 'D', 'F'];
          return gradeOrder.indexOf(c.grade) < gradeOrder.indexOf(top) ? c.grade : top;
        }, 'F')
      : '-',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">My Certificates</h1>
          <p className="text-gray-400 mt-1 text-sm">
            View and download your earned certificates
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">Total</p>
              <p className="text-3xl font-bold text-white mt-1">{stats.total}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center">
              <Award className="w-6 h-6 text-teal-400" />
            </div>
          </div>
        </div>

        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">Earned</p>
              <p className="text-3xl font-bold text-white mt-1">{stats.earned}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
              <Trophy className="w-6 h-6 text-emerald-400" />
            </div>
          </div>
        </div>

        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">Top Grade</p>
              <p className="text-3xl font-bold text-white mt-1">{stats.topGrade}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
              <Star className="w-6 h-6 text-amber-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Search certificates by title or certificate number..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            className="w-full pl-10 pr-4 py-2.5 bg-[#0d0d12] border border-white/[0.06] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-teal-500/50 text-sm"
          />
        </div>
      </div>

      {/* Certificates Grid */}
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
        </div>
      ) : filteredCertificates.length === 0 ? (
        <div className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-white/[0.04] flex items-center justify-center mx-auto mb-4">
            <Award className="w-8 h-8 text-gray-500" />
          </div>
          <p className="text-white font-semibold text-lg mb-2">No certificates yet</p>
          <p className="text-gray-400 text-sm">
            Complete exams with passing scores to earn certificates
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCertificates.map((certificate) => (
              <div
                key={certificate.id}
                className="bg-[#12121a]/80 backdrop-blur-sm border border-white/[0.06] rounded-xl overflow-hidden hover:border-white/[0.12] transition-all group"
              >
                {/* Certificate Preview Header */}
                <div className="h-32 bg-gradient-to-br from-teal-500/20 via-cyan-500/10 to-purple-500/20 relative flex items-center justify-center">
                  <Award className="w-16 h-16 text-white/20" />
                  <div className="absolute top-3 right-3 flex gap-1.5">
                    {certificate.blockchain_status === 'confirmed' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium border bg-purple-500/10 text-purple-400 border-purple-500/20" title={certificate.transaction_hash ? `TX: ${certificate.transaction_hash}` : 'Blockchain verified'}>
                        <ShieldCheck className="w-3 h-3" />
                        verified
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium border bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                      <CheckCircle className="w-3 h-3" />
                      issued
                    </span>
                  </div>
                  {certificate.grade && (
                    <div className={`absolute bottom-3 left-3 px-3 py-1.5 rounded-lg ${getGradeColor(certificate.grade)} font-bold text-lg`}>
                      {certificate.grade}
                    </div>
                  )}
                </div>

                {/* Certificate Info */}
                <div className="p-4">
                  <h3 className="font-semibold text-white mb-1 line-clamp-1">
                    {certificate.course_title}
                  </h3>
                  <p className="text-xs text-gray-500 mb-3">
                    #{certificate.certificate_number}
                  </p>

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Recipient</span>
                      <span className="text-white font-medium">
                        {certificate.user_name}
                      </span>
                    </div>
                    {certificate.completion_date && (
                      <div className="flex justify-between">
                        <span className="text-gray-400">Completed</span>
                        <span className="text-gray-300">
                          {format(new Date(certificate.completion_date), 'MMM dd, yyyy')}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-400">Issued</span>
                      <span className="text-gray-300">
                        {format(new Date(certificate.issued_at), 'MMM dd, yyyy')}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => handleDownload(certificate)}
                      disabled={downloading === certificate.id}
                      className="flex-1 px-3 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg font-medium text-sm hover:from-teal-600 hover:to-cyan-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {downloading === certificate.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      Download
                    </button>
                    <button
                      onClick={() => handleCopyVerificationLink(certificate)}
                      className="p-2 rounded-lg bg-white/[0.04] text-gray-400 hover:text-white hover:bg-white/[0.08] transition-colors"
                      title="Copy verification link"
                    >
                      {copiedId === certificate.id ? (
                        <Check className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <Share2 className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => setSelectedCertificate(certificate)}
                      className="p-2 rounded-lg bg-white/[0.04] text-gray-400 hover:text-white hover:bg-white/[0.08] transition-colors"
                      title="View details"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-400">
                Page {page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="flex items-center gap-1 px-3 py-2 bg-white/[0.04] border border-white/[0.06] rounded-lg text-sm text-gray-300 hover:bg-white/[0.08] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="flex items-center gap-1 px-3 py-2 bg-white/[0.04] border border-white/[0.06] rounded-lg text-sm text-gray-300 hover:bg-white/[0.08] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Certificate Details Modal */}
      {selectedCertificate && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#12121a] border border-white/[0.08] rounded-2xl shadow-2xl max-w-lg w-full">
            <div className="p-6 border-b border-white/[0.06]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center">
                    <Award className="w-6 h-6 text-teal-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Certificate Details</h2>
                    <p className="text-gray-400 text-sm">#{selectedCertificate.certificate_number}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedCertificate(null)}
                  className="p-2 rounded-lg bg-white/[0.04] text-gray-400 hover:bg-white/[0.08] hover:text-white transition-colors"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="p-4 bg-[#0d0d12] rounded-xl space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Title</span>
                  <span className="text-white font-medium">{selectedCertificate.course_title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Recipient</span>
                  <span className="text-white">{selectedCertificate.user_name}</span>
                </div>
                {selectedCertificate.grade && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Grade</span>
                    <span className={`px-2 py-0.5 rounded ${getGradeColor(selectedCertificate.grade)} font-bold`}>
                      {selectedCertificate.grade}
                    </span>
                  </div>
                )}
                {selectedCertificate.completion_date && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Completed</span>
                    <span className="text-white">{format(new Date(selectedCertificate.completion_date), 'PPP')}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-400">Issued</span>
                  <span className="text-white">{format(new Date(selectedCertificate.issued_at), 'PPP')}</span>
                </div>
                {selectedCertificate.blockchain_status && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Blockchain</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      selectedCertificate.blockchain_status === 'confirmed' ? 'bg-emerald-500/10 text-emerald-400'
                        : selectedCertificate.blockchain_status === 'pending' ? 'bg-amber-500/10 text-amber-400'
                        : 'bg-gray-500/10 text-gray-400'
                    }`}>
                      <ShieldCheck className="w-3 h-3 inline mr-1" />
                      {selectedCertificate.blockchain_status}
                    </span>
                  </div>
                )}
                {selectedCertificate.transaction_hash && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">TX Hash</span>
                    <span className="text-white/60 font-mono text-xs truncate max-w-[200px]" title={selectedCertificate.transaction_hash}>
                      {selectedCertificate.transaction_hash.slice(0, 16)}...
                    </span>
                  </div>
                )}
              </div>

              {/* Verification Link */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Verification Link</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={`${window.location.origin}/verify/${selectedCertificate.certificate_number}`}
                    className="flex-1 px-4 py-2.5 bg-[#0d0d12] border border-white/[0.06] rounded-xl text-gray-400 text-sm"
                  />
                  <button
                    onClick={() => handleCopyVerificationLink(selectedCertificate)}
                    className="px-4 py-2.5 bg-white/[0.04] border border-white/[0.06] rounded-xl text-gray-300 hover:bg-white/[0.08] transition-colors flex items-center gap-2"
                  >
                    {copiedId === selectedCertificate.id ? (
                      <>
                        <Check className="w-4 h-4 text-emerald-400" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-white/[0.06] flex gap-3">
              <button
                onClick={() => setSelectedCertificate(null)}
                className="flex-1 px-4 py-2.5 bg-white/[0.04] border border-white/[0.06] text-gray-300 rounded-xl hover:bg-white/[0.08] font-medium text-sm"
              >
                Close
              </button>
              <button
                onClick={() => handleDownload(selectedCertificate)}
                disabled={downloading === selectedCertificate.id}
                className="flex-1 px-4 py-2.5 bg-teal-500 text-white rounded-xl hover:bg-teal-600 font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {downloading === selectedCertificate.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyCertificatesPage;
